import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User } from '@/types'
import { queryClient } from '@/lib/queryClient'
import { resetSessionBootstrap } from '@/lib/bootstrap-session'
import { clearAllDedupeRequests } from '@/lib/dedupe-request'
import { useWabaStore } from '@/stores/wabaStore'
import { usePermissionStore } from '@/stores/permissionStore'
import { connectNotificationSocket, disconnectNotificationSocket } from '@/lib/notification-ws'

interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  loginAttempts: number
  lastLoginAttempt: number | null

  setAuth: (user: User, accessToken: string, refreshToken: string) => void
  setAccessToken: (accessToken: string) => void
  updateUser: (user: Partial<User>) => void
  logout: () => void
  incrementLoginAttempts: () => void
  resetLoginAttempts: () => void
  isLockedOut: () => boolean
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      loginAttempts: 0,
      lastLoginAttempt: null,

      setAuth: (user, accessToken, refreshToken) => {
        localStorage.setItem('access_token', accessToken)
        localStorage.setItem('refresh_token', refreshToken)
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          loginAttempts: 0,
          lastLoginAttempt: null,
        })
        // Connect notification WebSocket once on login — persistent until logout
        connectNotificationSocket(accessToken)
      },

      // Refresh path (axios interceptor / WS reconnect) calls this when it
      // obtains a new access token so persisted state + hooks stay in sync.
      setAccessToken: (accessToken) => {
        localStorage.setItem('access_token', accessToken)
        set({ accessToken })
      },

      updateUser: (updates) => {
        const { user } = get()
        if (user) {
          set({ user: { ...user, ...updates } })
        }
      },

      logout: () => {
        // Disconnect notification WebSocket before clearing tokens
        disconnectNotificationSocket()

        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')

        // Clear React Query cache to prevent stale data across accounts
        queryClient.clear()
        resetSessionBootstrap()
        clearAllDedupeRequests()

        // Reset other persisted stores
        useWabaStore.getState().reset()
        usePermissionStore.getState().clearAllPermissionList()

        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        })
      },

      incrementLoginAttempts: () => {
        set((state) => ({
          loginAttempts: state.loginAttempts + 1,
          lastLoginAttempt: Date.now(),
        }))
      },

      resetLoginAttempts: () => {
        set({ loginAttempts: 0, lastLoginAttempt: null })
      },

      isLockedOut: () => {
        const { loginAttempts, lastLoginAttempt } = get()
        if (loginAttempts < 5) return false
        if (!lastLoginAttempt) return false
        const lockoutDuration = 15 * 60 * 1000 // 15 minutes
        return Date.now() - lastLoginAttempt < lockoutDuration
      },
    }),
    {
      name: 'auth-store',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        loginAttempts: state.loginAttempts,
        lastLoginAttempt: state.lastLoginAttempt,
      }),
    },
  ),
)
